import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { AWS_URL } from "../../Constant";
import { v4 as uuidv4 } from "uuid";

const AccountsExpenses = () => {
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [allExpenses, setAllExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [, setLoading] = useState(false);
const [dateFilter, setDateFilter] = useState(""); // single date
  const [monthFilter, setMonthFilter] = useState(""); // yyyy-MM

  // Fetch expenses once
  const fetchExpenses = async () => {
    setLoading(true);
    const jwtToken = Cookies.get("jwt_token");
    const url = `${AWS_URL}/getAllStoreExpenses`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();

        // Flatten expenses
        const combinedExpenses = data.reduce((acc, store) => {
          const storeExpenses = (store.Expenses || []).map((exp) => ({
            ...exp,
            RetailStoreCode: store.RetailStoreCode,
            StoreName: store.storeName,
            _id: exp._id || uuidv4(),
          }));
          return [...acc, ...storeExpenses];
        }, []);

        setAllExpenses(combinedExpenses);

        // Extract unique branches
        const uniqueBranches = data.map((store) => ({
          storeCode: store.RetailStoreCode,
          storeName: store.storeName,
        }));
        setBranches(uniqueBranches);
      }
    } catch (error) {
} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  // Filtering
  useEffect(() => {
    let filtered = [...allExpenses];

    // Branch filter
    if (selectedBranch !== "ALL") {
      filtered = filtered.filter(
        (exp) => exp.RetailStoreCode === selectedBranch
      );
    }

    // Date filter
    if (dateFilter) {
      filtered = filtered.filter((exp) => exp.date === dateFilter);
    }

    // Month filter
    if (monthFilter) {
      filtered = filtered.filter(
        (exp) => exp.date && exp.date.startsWith(monthFilter)
      ); // exp.date like "2025-09-26"
    }

    setFilteredExpenses(filtered);
  }, [selectedBranch, dateFilter, monthFilter, allExpenses]);

  // Group expenses by branch
  const groupByBranch = (expenses) => {
    return expenses.reduce((acc, exp) => {
      if (!acc[exp.RetailStoreCode]) {
        acc[exp.RetailStoreCode] = {
          branchName: exp.StoreName,
          branchCode: exp.RetailStoreCode,
          expenses: [],
        };
      }
      acc[exp.RetailStoreCode].expenses.push(exp);
      return acc;
    }, {});
  };

  const groupedData = groupByBranch(filteredExpenses);

  // Calculate totals
  const branchTotals = {};
  Object.keys(groupedData).forEach((branch) => {
    branchTotals[branch] = groupedData[branch].expenses.reduce(
      (sum, exp) => sum + (Number(exp.amount) || 0),
      0
    );
  });
  const grandTotal = Object.values(branchTotals).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-indigo-500 to-pink-500 text-white p-2 rounded-lg">
        Accounts - Expenses Report
      </h1>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow mb-6 flex flex-wrap gap-4 items-center">
        <div>
          <label className="text-sm font-medium">Branch:</label>
          <select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            className="border rounded-lg px-3 py-2 ml-2"
          >
            <option value="">-- Select Branch --</option>
            <option value="ALL">All Branches</option>
            {branches.map((b) => (
              <option key={b.storeCode} value={b.storeCode}>
                {b.storeName} ({b.storeCode})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-sm font-medium">Date:</label>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 ml-2"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Month:</label>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 ml-2"
          />
        </div>
      </div>

      {/* Expenses Display */}
      {!selectedBranch ? (
        <p className="text-center text-lg">Please select a branch</p>
      ) : filteredExpenses.length === 0 ? (
        <p className="text-center text-lg">No expenses found</p>
      ) : (
        <div className="space-y-6">
          {Object.values(groupedData).map((branch) => (
            <div
              key={branch.branchCode}
              className="bg-white rounded-xl shadow p-4 border border-gray-200"
            >
              <h2 className="text-xl font-semibold mb-2">
                {branch.branchName} ({branch.branchCode})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-200 text-sm text-gray-700">
                      <th className="border p-2">S.No</th>
                      <th className="border p-2">Date</th>
                      <th className="border p-2">Item</th>
                      <th className="border p-2">Amount</th>
                      <th className="border p-2">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branch.expenses.map((exp, i) => (
                      <tr
                        key={exp._id}
                        className="hover:bg-gray-50 text-sm text-gray-800"
                      >
                        <td className="border p-2">{i + 1}</td>
                        <td className="border p-2">{exp.date}</td>
                        <td className="border p-2">{exp.item}</td>
                        <td className="border p-2 font-semibold">
                          ₹{exp.amount}
                        </td>
                        <td className="border p-2">{exp.description}</td>
                      </tr>
                    ))}
                    {/* Branch total row */}
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan="3" className="border p-2 text-right">
                        Total:
                      </td>
                      <td className="border p-2">
                        ₹{branchTotals[branch.branchCode]}
                      </td>
                      <td className="border p-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Grand total */}
          <div className="bg-green-100 p-4 rounded-lg font-bold text-lg text-right">
            Grand Total: ₹{grandTotal}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsExpenses;
